// dependencies
import React, { useState, useCallback, useRef, useEffect } from "react";
import { findNodeHandle } from "react-native";
import { useIntl } from "react-intl";
import { css, StyleSheet } from "aphrodite";

// components
import TextInput from "@cloudspire/legacy-resources/src/components/TextInput2";
import Popover3 from "@cloudspire/legacy-resources/src/components/Popover3";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import { IconCrossO, IconDatesO } from "@cloudspire/legacy-svg-icon-library";
import { FieldError } from "../../components/Field";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";

// containers
import BookingDatePicker from "@cloudspire/legacy-resources/src/containers/BookingDatePicker";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// libraries
import EnhancedDate from "@cloudspire/legacy-resources/src/libraries/EnhancedDate";
import { reactMultipleRefs } from "../../libraries";

const styles = StyleSheet.create({
  clear: {
    cursor: "pointer",
  },
});

type PropsType = {
  inputCheckinRef: any;
  inputCheckoutRef: any;
  checkinErrorList?: Array<string>;
  checkoutErrorList?: Array<string>;
  name: string;
  initialDate: EnhancedDate;
  min?: EnhancedDate;
  start: EnhancedDate;
  end: EnhancedDate;
  roomReference: string;
  channelId: string;
  required?: boolean;
  formatDateOptions?;
  shouldCloseWhenStartAndEndSelected?: boolean;
  shouldRenderApply?: boolean;
  shouldRenderLegend?: boolean;
  onSelect: (event: {
    target: { name: string; value: { start; end } };
  }) => void;
};

function BookingDatePickerWithTextInput(props: PropsType) {
  const {
    initialDate,
    name,
    start,
    end,
    min,
    required = false,
    roomReference,
    channelId,
    checkinErrorList = [],
    checkoutErrorList = [],
    onSelect,
    formatDateOptions = {
      weekday: "long",
      month: "short",
      day: "numeric",
    },
    inputCheckinRef,
    inputCheckoutRef,
    shouldCloseWhenStartAndEndSelected = true,
    shouldRenderLegend,
    shouldRenderApply = false,
  } = props;

  const [referenceElement, setReferenceElement] = useState(null);

  const [date, setDate] = useState(initialDate.startOfDays());

  const intl = useIntl();

  const { matches } = useViewportMedia({ queries: [{ minWidth: 350 }] });

  const $checkinField = useRef<any>();

  const $checkoutField = useRef<any>();

  const $content = useRef();

  const $view = useRef();

  /**
   * Gère le focus sur l'input d'arrivé.
   */
  const handleFocusCheckin = useCallback((event) => {
    event.stopPropagation();

    setReferenceElement($checkinField.current);
  }, []);

  /**
   * Gère le focus sur l'input de départ.
   */
  const handleFocusCheckout = useCallback((event) => {
    event.stopPropagation();

    setReferenceElement($checkoutField.current);
  }, []);

  /**
   * Gère le changement de date.
   */
  const handleChangeDate = (newDate) => {
    setDate(newDate);
  };

  /**
   * Gère le clic en dehors des inputs et du date-picker.
   */
  const handleClick = useCallback(
    (event: FocusEvent) => {
      if (null !== referenceElement) {
        const view = findNodeHandle($view.current) as unknown as HTMLElement;
        const content = findNodeHandle(
          $content.current
        ) as unknown as HTMLElement;

        if (
          !view.contains(event.target as HTMLElement) &&
          !content.contains(event.target as HTMLElement)
        ) {
          setReferenceElement(null);
        }
      }
    },
    [referenceElement]
  );

  const handlePressApply = useCallback(() => {
    setReferenceElement(null);
  }, []);

  const handleSelect = ({ start, end }) => {
    if (null !== start && null === end) {
      $checkoutField.current.focus();
    }

    if (shouldCloseWhenStartAndEndSelected && null !== start && null !== end) {
      setReferenceElement(null);
    }

    onSelect({ target: { name, value: { start, end } } });
  };

  const handleClearStart = () => {
    onSelect({ target: { name, value: { start: null, end: null } } });
  };

  const handleClearEnd = () => {
    onSelect({ target: { name, value: { start, end: null } } });
  };

  useEffect(() => {
    if (referenceElement) {
      document.body.addEventListener("click", handleClick);
    }

    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }, [referenceElement, handleClick]);

  const $wrapper = useRef();

  return (
    <div ref={$wrapper}>
      <ListItem>
        <Popover3
          visible={null !== referenceElement}
          reference={$wrapper.current}
          usePreventOverlow={true}
          popover={
            <div ref={$content}>
              <BookingDatePicker
                min={min}
                date={date}
                channelId={channelId}
                roomReference={roomReference}
                start={start}
                end={end}
                shouldRenderApply={shouldRenderApply}
                shouldRenderLegend={shouldRenderLegend}
                onSelect={handleSelect}
                onChangeDate={handleChangeDate}
                onPressApply={handlePressApply}
              />
            </div>
          }
          container={
            <div ref={$view}>
              <Row type="cell" maxColumns={matches ? 2 : 1} gutter={0.5}>
                <Col>
                  <List gutter={0.3125}>
                    <ListItem>
                      <TextInput
                        ref={reactMultipleRefs([
                          $checkinField,
                          inputCheckinRef,
                        ])}
                        name="checkin"
                        id="formCheckin"
                        required={required}
                        shouldRenderRequiredIcon={false}
                        readOnly={true}
                        value={
                          start
                            ? intl.formatDate(
                                start.getNativeDate(),
                                formatDateOptions
                              )
                            : ""
                        }
                        rightIcon={
                          <div
                            className={css(styles.clear)}
                            onClick={handleClearStart}
                          >
                            <IconCrossO />
                          </div>
                        }
                        leftIcon={<IconDatesO />}
                        label={intl.formatMessage({
                          defaultMessage: "Arrivée",
                        })}
                        onFocus={handleFocusCheckin}
                      />
                    </ListItem>
                    {checkinErrorList.length > 0 && (
                      <FieldError errorList={checkinErrorList} />
                    )}
                  </List>
                </Col>

                <Col>
                  <List gutter={0.3125}>
                    <ListItem>
                      <TextInput
                        ref={reactMultipleRefs([
                          $checkoutField,
                          inputCheckoutRef,
                        ])}
                        name="checkout"
                        id="formCheckout"
                        required={required}
                        shouldRenderRequiredIcon={false}
                        readOnly={true}
                        value={
                          end
                            ? intl.formatDate(
                                end.getNativeDate(),
                                formatDateOptions
                              )
                            : ""
                        }
                        rightIcon={
                          <div
                            className={css(styles.clear)}
                            onClick={handleClearEnd}
                          >
                            <IconCrossO />
                          </div>
                        }
                        leftIcon={<IconDatesO />}
                        label={intl.formatMessage({
                          defaultMessage: "Départ",
                        })}
                        onFocus={handleFocusCheckout}
                      />
                    </ListItem>

                    {checkoutErrorList.length > 0 && (
                      <ListItem>
                        <FieldError errorList={checkoutErrorList} />
                      </ListItem>
                    )}
                  </List>
                </Col>
              </Row>
            </div>
          }
        />
      </ListItem>
    </div>
  );
}

export default BookingDatePickerWithTextInput;
